import React from 'react';
import PropTypes from 'prop-types';
import { useCombobox, useMultipleSelection } from 'downshift';
import isEmpty from 'lodash/isEmpty';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import head from 'lodash/head';
import classNames from 'classnames';

import { Input } from 'antd';
import { Button } from 'components/AntD';
import Option from './Option';
import SelectedItem from './SelectedItem';
import SplashScreen from 'components/SplashScreen';
import './ComboBox.less';

function ComboBox({
  hasPreview,
  renderCustomActions,
  availableAnswerOptions,
  placeholder,
  selectedItems: selectedOptions,
  handleSearch,
  addAnswerFunction,
  titleKey,
  subtitleKey,
  idKey,
  noResultsString,
  noResultsAction,
  noResultsCtaText,
  isLoading,
  id,
  alreadyAddedText,
}) {
  const [inputValue, setInputValue] = React.useState('');
  const [lastSelected, setLastSelected] = React.useState();
  const onSelectedItemsChange = React.useCallback(
    ({ selectedItems }) => {
      addAnswerFunction(selectedItems, lastSelected);
      setLastSelected(null);
    },
    [addAnswerFunction, lastSelected]
  );
  const {
    getSelectedItemProps,
    getDropdownProps,
    addSelectedItem,
    removeSelectedItem,
    selectedItems,
  } = useMultipleSelection({
    selectedItems: selectedOptions,
    onSelectedItemsChange,
  });
  // Added items should go to the bottom of the options list
  const sortedData = React.useMemo(
    () =>
      [...availableAnswerOptions].sort(item =>
        selectedItems.find(option => get(option, idKey) === get(item, idKey)) ? 1 : -1
      ),
    [availableAnswerOptions, selectedItems, idKey]
  );

  const {
    isOpen,
    highlightedIndex,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    getItemProps,
    openMenu,
    closeMenu,
  } = useCombobox({
    inputValue,
    items: sortedData,
    defaultSelectedItem: head(sortedData),
    onStateChange: ({ type, selectedItem }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputBlur:
          if (selectedItem) {
            setSearchKeyword({ target: { value: '' } });
            addSelectedItem(selectedItem);
            setLastSelected(selectedItem);
          }

          break;
        default:
          break;
      }
    },
  });

  const noResultsActionWrapper = React.useCallback(() => {
    setInputValue('');
    noResultsAction(inputValue);
  }, [inputValue, noResultsAction]);
  const addSelectedCallback = React.useCallback(
    item => {
      addSelectedItem(item);
      setLastSelected(item);
      closeMenu();
    },
    [addSelectedItem, closeMenu]
  );
  const keywordSearchCallback = React.useMemo(
    () =>
      debounce(keyword => {
        handleSearch(keyword);
      }, 250),
    [handleSearch]
  );
  const setSearchKeyword = React.useCallback(
    e => {
      setInputValue(e.target.value);
      keywordSearchCallback(e.target.value);
    },
    [keywordSearchCallback]
  );

  return (
    <div className="combo-box tw-relative" tabIndex={-1}>
      <div {...getComboboxProps()}>
        <Input
          placeholder={placeholder}
          suffix={
            inputValue &&
            noResultsAction &&
            isEmpty(availableAnswerOptions) && (
              <Button
                className="no-result-action-btn"
                variant="contained"
                color="primary"
                size="small"
                loading={isLoading}
                onClick={noResultsActionWrapper}
              >
                {noResultsCtaText}
              </Button>
            )
          }
          {...getInputProps({ ...getDropdownProps(), refKey: 'inputRef', id })}
          onChange={setSearchKeyword}
          value={inputValue}
          onClick={openMenu}
          fullWidth
        />
      </div>
      <div
        className={classNames('wrapper tw-absolute tw-w-full', {
          'tw-hidden': !isOpen,
        })}
      >
        <ul
          {...getMenuProps({
            className: 'item-list tw-list-none tw-ml-0',
          })}
        >
          {isLoading && (
            <li>
              <SplashScreen />
            </li>
          )}
          {isEmpty(availableAnswerOptions) && (
            <li className="p-2">
              <em className="tw-text-gray tw-text-center">{noResultsString}</em>
            </li>
          )}
          {sortedData.map((item, index) => {
            const title = selectedOptions.find(option => get(option, idKey) === get(item, idKey))
              ? alreadyAddedText
              : null;

            return (
              // eslint-disable-next-line
              <li
                className="p-1"
                key={get(item, idKey)}
                {...(title
                  ? {}
                  : getItemProps({
                      item,
                      index,
                      onClick: title ? null : () => addSelectedCallback(item),
                    }))}
                title={title}
              >
                <Option
                  title={get(item, titleKey)}
                  subtitle={
                    title ||
                    (typeof subtitleKey === 'function' ? subtitleKey(item) : get(item, subtitleKey))
                  }
                  isActive={highlightedIndex === index}
                  item={item}
                  hasPreview={hasPreview}
                  disabled={!!title}
                />
              </li>
            );
          })}
        </ul>
      </div>
      {!isEmpty(selectedItems) && (
        <ul className="selected-item-list tw-mt-xs tw-list-none tw-ml-0">
          {selectedItems.map((item, index) => (
            <li
              className="tw-mb-xs"
              key={get(item, idKey)}
              {...getSelectedItemProps({ item, index })}
            >
              <SelectedItem
                item={item}
                title={get(item, titleKey)}
                subtitle={
                  typeof subtitleKey === 'function' ? subtitleKey(item) : get(item, subtitleKey)
                }
                removeSelectedItem={removeSelectedItem}
                renderCustomActions={renderCustomActions}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

ComboBox.propTypes = {
  placeholder: PropTypes.string,
  id: PropTypes.string,
  selectedTitle: PropTypes.string,
  selectedItems: PropTypes.array,
  availableAnswerOptions: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  addAnswerFunction: PropTypes.func.isRequired,
  hasPreview: PropTypes.bool,
  preventEnterBubble: PropTypes.bool,
  renderCustomActions: PropTypes.func,
  noResultsAction: PropTypes.func,
  noResultsCtaText: PropTypes.string,
  noResultsString: PropTypes.string,
  titleKey: PropTypes.string,
  subtitleKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  idKey: PropTypes.string,
  alreadyAddedText: PropTypes.string,
  handleSearch: PropTypes.func,
};

ComboBox.defaultProps = {
  titleKey: 'title',
  subtitleKey: 'subtitle',
  idKey: 'key',
  alreadyAddedText: 'You have already added this item',
  placeholder: 'Search for or enter name of record…',
  selectedItems: [],
  hasPreview: true,
  preventEnterBubble: false,
  isLoading: false,
  renderCustomActions: (item, removeSelectedItem) => (
    <Button
      variant="text"
      icon={<i className="fa fa-times" />}
      title="Remove"
      onClick={e => {
        e.stopPropagation();
        removeSelectedItem(item, e);
      }}
    />
  ),
  noResultsCtaText: 'Action',
  noResultsString: 'No Results',
  handleSearch: () => void 0,
};

export default ComboBox;
